import React, { Children, useEffect, useState } from 'react'
import { useWindowSize } from '../../hooks/useWindowSize'
import { SetState } from '../../vite-env'
import { SliderContent } from '../Animations/Slider/SliderContent'
import { navigateToSlideIndex } from '../Animations/Slider/slider.helpers'
import { Step, StepProps } from './Step'
import { StepperFooter, StepperHeader } from './components'
import { StepperAccordion } from './components/Header/StepperAccordion'
import { UseFormReturn } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

export type StepperVariant = 'tabs' | 'accordion'

type Props = (
  | {
      currentStep: number
      setCurrentStep: SetState<number>
    }
  | {
      defaultStep?: number
    }
) & {
  className?: string
  sliderClasses?: string
  children: (React.FunctionComponentElement<StepProps> | false)[] // Children Should have step props
  hideHeader?: boolean
  fullPage?: boolean
  hideFooter?: boolean
  /** Opt-in: pair with `<Stepper.Step fields={[...]}>` to have the
   *  footer auto-gate Next on rhf errors for those field paths and
   *  trigger validation on click. Most consumers should compute
   *  `disabled` per step instead — this is left in for convenience. */
  form?: UseFormReturn<any>
  addMarginAndSoftenEdges?: boolean
  /** Layout of the step header. `tabs` (default) renders a horizontal
   * progress bar across the top; `accordion` renders each step as a
   * vertical accordion item with only the current step expanded. */
  variant?: StepperVariant
  /** Accordion variant only — when true, the user can jump to any
   * step regardless of completion state. Useful for edit flows. */
  allowJumpToAnyStep?: boolean
  /** Accordion variant only — when true, steps that the user has not
   * yet reached are not rendered. Steps reveal one at a time as the
   * user progresses. Going back to a previous step does not unhide
   * already-revealed later steps. */
  hideUntouchedSteps?: boolean
}

export const Stepper = (props: Props) => {
  const controlled = 'currentStep' in props && 'setCurrentStep' in props

  // const internalState = useState((props.defaultStep || 1) - 1)
  const [currentStepIndex, setCurrentStepIndex] = controlled
    ? [props.currentStep, props.setCurrentStep]
    : useState((props.defaultStep || 1) - 1)

  const [stepPropsList, setStepPropsList] = useState<StepProps[]>([])

  const [windowHeight] = useWindowSize()

  // Follow the current step by TITLE so list changes can’t bounce the user
  const steps = Children.toArray(props.children).filter(
    (child): child is React.ReactElement<StepProps> =>
      React.isValidElement(child),
  )
  const stepTitles = steps.map((child) => String(child.props.title))
  const stepTitlesKey = stepTitles.join('\n')
  const stepKeepMounted = steps.map((child) => !!child.props.keepMounted)
  const currentStepMemory = React.useRef({
    titlesKey: stepTitlesKey,
    title: stepTitles[currentStepIndex],
    index: currentStepIndex,
  })
  useEffect(() => {
    const previous = currentStepMemory.current
    const listChanged = previous.titlesKey !== stepTitlesKey
    // Only follow when nothing moved the index this commit already
    const stayedPut = previous.index === currentStepIndex
    const followedIndex =
      listChanged && stayedPut && previous.title != null
        ? stepTitles.indexOf(previous.title)
        : -1
    const follow = followedIndex !== -1 && followedIndex !== currentStepIndex
    // Title vanished while stationary: clamp into range (floor 0 for [])
    const nextIndex = follow
      ? followedIndex
      : Math.max(0, Math.min(currentStepIndex, stepTitles.length - 1))
    if (nextIndex !== currentStepIndex) setCurrentStepIndex(nextIndex)
    currentStepMemory.current = {
      titlesKey: stepTitlesKey,
      title: stepTitles[nextIndex],
      index: nextIndex,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepTitlesKey, currentStepIndex])


  const variant: StepperVariant = props.variant ?? 'tabs'

  // Visited-steps high-water mark for hideUntouchedSteps
  const [maxStepReached, setMaxStepReached] = useState(currentStepIndex)
  // History-dependent, so state (not derivable via useMemo)
  useEffect(() => {
    setMaxStepReached((prev) => Math.max(prev, currentStepIndex))
  }, [currentStepIndex])

  useEffect(() => {
    setStepPropsList(
      Children.toArray(props.children)
        .filter((child): child is React.ReactElement<StepProps> =>
          React.isValidElement(child),
        )
        .map((child, i) => ({
          ...child.props,
          completed: i < currentStepIndex,
        })),
    )
  }, [props.children, currentStepIndex])

  const onClickTitle = async (stepIndex: number) => {
    // A loading step is mid-submit; title clicks must wait
    if (stepPropsList[currentStepIndex]?.loading) return
    if (stepIndex > currentStepIndex) {
      // Leaving step's onNext can veto by returning false
      const result = await stepPropsList[currentStepIndex]?.onNext?.()
      if (result === false) return
      setStepPropsList((prev) =>
        prev.map((step, i) =>
          i === currentStepIndex ? { ...step, completed: true } : step,
        ),
      )
    }

    setCurrentStepIndex(() =>
      navigateToSlideIndex(stepIndex, currentStepIndex, stepPropsList),
    )
  }

  const isAccordion = variant === 'accordion'

  return (
    <section
      style={
        props.fullPage ? { height: windowHeight, maxHeight: windowHeight } : {}
      }
      className={twMerge(
        `w-full h-full flex flex-col justify-between items-stretch grow basis-0 overflow-clip`,
        props.addMarginAndSoftenEdges && '-m-8 p-8',
        props.className,
      )}>
      {!isAccordion && !props.hideHeader && (
        <StepperHeader
          currentStep={currentStepIndex}
          stepPropsList={stepPropsList}
          onClickTitle={onClickTitle}
        />
      )}

      {isAccordion ? (
        <StepperAccordion
          form={props.form}
          currentStep={currentStepIndex}
          stepPropsList={stepPropsList}
          steps={Children.toArray(props.children)}
          allowJumpToAnyStep={props.allowJumpToAnyStep}
          hideUntouched={props.hideUntouchedSteps}
          maxStepReached={maxStepReached}
          onClickTitle={onClickTitle}
        />
      ) : (
        <SliderContent
          addMarginAndSoftenEdges={props.addMarginAndSoftenEdges}
          selectedSlide={currentStepIndex}
          keepMounted={stepKeepMounted}
          className={props.sliderClasses}>
          {props.children}
        </SliderContent>
      )}

      {!props.hideFooter && (
        <StepperFooter
          currentStepIndex={currentStepIndex}
          setCurrentStepIndex={setCurrentStepIndex}
          stepProps={stepPropsList}
          form={props.form}
        />
      )}
    </section>
  )
}

Stepper.Step = Step
